import { UseFormGetValues } from "react-hook-form";
import { format, isBefore, isValid, parse, startOfToday } from "date-fns";

import { UserType } from "src/types/users";
import {
  AddCaseProps,
  CaseAttributesProps,
  CaseDoctorProps,
  InsuranceAttributesProps,
  PatientAddress,
  PatientAttributes,
  PatientPhoneNumber,
  PrescriptionAttributesType,
  PrescriptionInformationType,
} from "src/pages/addCase/types";
import { CaseCloneResponseType } from "src/pages/cloneCase/types";
import { SHIP_TO } from "src/constants/cases";
import { failureReasons } from "src/components/addCase/medCheckBoxList";

function detectDateFormat(dateString: string) {
  // Extend this list with the formats you expect
  const possibleFormats = [
    "yyyy-MM-dd",
    "dd/MM/yyyy",
    "MM/dd/yyyy",
    "dd-MM-yyyy",
    "MM-dd-yyyy",
    "yyyy/MM/dd",
    "dd MMM yyyy",
    "MMM dd, yyyy",
  ];

  for (const fmt of possibleFormats) {
    try {
      const parsed = parse(dateString, fmt, new Date());
      // If parsing succeeded and returned a valid Date
      if (isValid(parsed)) {
        return fmt;
      }
    } catch (err) {
      // ignore failed format
    }
  }

  return null; // No matching format found
}

export function populatingCloneCase(
  { data }: CaseCloneResponseType,
  userData: UserType,
  getValues: UseFormGetValues<AddCaseProps>,
): AddCaseProps {
  const caseFormData: AddCaseProps = {} as AddCaseProps;

  if (data.case) {
    caseFormData.mos = getValues("mos");
    caseFormData.user_id = userData ? userData.id : 0;
    caseFormData.creator_name = userData ? userData.name : "";
    caseFormData.submission_source = data.case.submission_source || "provider";
    caseFormData.category = "rx";
    caseFormData.uploaded_documents = [];
  }

  caseFormData.api_pa_approved = data.pa_approved;

  caseFormData.case_attributes = {} as CaseAttributesProps;
  if (data.case_attributes.length > 0) {
    data.case_attributes.forEach((item) => {
      if (item.name === "pa_approved") {
        caseFormData.case_attributes.pa_approved = item.value;
      }
      if (item.name === "pa_approved_date") {
        caseFormData.case_attributes.pa_approved_date = item.value;
      }
    });
  }

  // if(caseFormData.patient_attributes){
  //   if(data.caregiver_name && data.caregiver_name.length > 0){
  //     caseFormData.patient_attributes.caregiver_name = data.caregiver_name;
  //   }
  //   if(data.caregiver_number && data.caregiver_number.length > 0){
  //     caseFormData.patient_attributes.caregiver_number = data.caregiver_number;
  //   }
  //   if(data.relation_to_patient && data.relation_to_patient.length > 0){
  //     caseFormData.patient_attributes.relation_to_patient = data.relation_to_patient;
  //   }
  // }

  if (data.soc) {
    const isActualShipTo = SHIP_TO.find(
      (soc) => soc?.value === data.soc[0]?.value,
    );
    if (isActualShipTo) {
      caseFormData.case_attributes.ship_to = data.soc[0].value;
    } else {
      caseFormData.case_attributes.ship_to = "other";
    }
  }

  if (data.prescription_information_attributes) {
    caseFormData.prescription_information_attributes =
      {} as PrescriptionInformationType;
    caseFormData.prescription_information_attributes.additional_units =
      String(data.prescription_information_attributes.additional_units ?? "");
    caseFormData.prescription_information_attributes.inhale_units =
      String(data.prescription_information_attributes.inhale_units ?? "");
    caseFormData.prescription_information_attributes.last_fev1_date =
      data.prescription_information_attributes.date_of_last_fev1;
    caseFormData.prescription_information_attributes.refills =
      String(data.prescription_information_attributes.refills ?? "");
    caseFormData.prescription_information_attributes.total_daily_units =
      String(data.prescription_information_attributes.total_daily_units ?? "");

    const priorTreatmentApplied = data.prior_treatment_applied_attributes || [];
    caseFormData.prescription_information_attributes.applicable_to_patient =
      priorTreatmentApplied.map((item) => item.reason).filter(Boolean);
    const priorTreatmentReasons = Array.isArray(
      data?.prior_treatment_reason_attributes,
    )
      ? data.prior_treatment_reason_attributes
      : [];
    const NEEDLE_PHOBIA_REASON = "needle phobia and unable to self-inject";
    const needlePhobiaItem = priorTreatmentReasons.find((item: any) => {
      return item.reason?.trim().toLowerCase() === NEEDLE_PHOBIA_REASON;
    });
    caseFormData.prescription_information_attributes = {
      ...(caseFormData.prescription_information_attributes || {}),

      reasons_alternative_insulins_not_effective: {
        main: priorTreatmentReasons
          .filter((item: any) => {
            return item.reason?.trim().toLowerCase();
          })
          .map((item: any) => item.reason),

        needle_phobia: needlePhobiaItem?.sub_reasons || [],
      },
    };
    const priorTreatmentAttributes: any[] = Array.isArray(
  data?.prior_treatment_attributes
)
  ? data.prior_treatment_attributes
  : [];

const KNOWN_MEDS = [
  "novolog", "humulin", "fiasp", "aspart", "lispro", "apidra", "two_orals_anti_diabetics",
];
const normalizeMed = (name: string) =>
  name.toLowerCase().replace(/\s+/g, "_").replace(/-/g, "_");

const resolveFailureReason = (reason: string): { reason: string; reasonOther: string } => {
  const knownReasons = failureReasons.filter((r) => r !== "Other");
  if (knownReasons.includes(reason)) return { reason, reasonOther: "" };
  return { reason: "Other", reasonOther: reason };
};

const diureticsTried = priorTreatmentAttributes.reduce<Record<string, any>>(
  (acc, item) => {
    const rawName: string = item.drug_name;
    if (!rawName) return acc;

    const key = normalizeMed(rawName);
    const { reason, reasonOther } = resolveFailureReason(item.failure_reason || "");

    if (KNOWN_MEDS.includes(key)) {
      acc[key] = true;
      acc[`${key}_props`] = {
        [`${key}_failure_reason`]: reason,
        [`${key}_failure_reason_other`]: reasonOther,
        [`${key}_tried_date`]: item.date_tried || "",
      };
    } else {
      acc["other"] = true;
      acc["other_props"] = {
        other_name: rawName,
        other_failure_reason: reason,
        other_failure_reason_other: reasonOther,
        other_tried_date: item.date_tried || "",
      };
    }

    return acc;
  },
  {}
);


caseFormData.prescription_information_attributes.cp_diuretics = {
  diuretics_tried: diureticsTried as any,
};
  }

  caseFormData.prescription_attributes = {
    written_date: format(new Date(), "MM/dd/yyyy"),
  } as PrescriptionAttributesType;

  if (data.prescription) {
    const diagnosis = data.prescription.diagnosis_code;
    caseFormData.prescription_attributes.diagnosis_code =
      diagnosis !== "" ? diagnosis.split(",") : [];
    caseFormData.prescription_attributes.note = data.prescription.note;
    caseFormData.prescription_attributes.direction =
      data.prescription.direction;
      caseFormData.prescription_attributes.packaging_size =
      data.prescription.packaging_size ?? "";
    caseFormData.prescription_attributes.qty = String(
      data.prescription.qty ?? "",
    );
    

    const drug_name = data.prescription.product;
    caseFormData.prescription_attributes.drug_name = drug_name;
  }

  if (data.prescription_attributes) {
    
    data.prescription_attributes.forEach((attr) => {
      if (attr.name === "scheduled_date_of_service") {
        if (attr.value) {
          const dateformat = detectDateFormat(attr.value);
          if (dateformat) {
            const scheduled_date = parse(attr.value, dateformat, new Date());
            if (!isBefore(scheduled_date, startOfToday())) {
              caseFormData.prescription_attributes.scheduled_date_of_service =
                format(parse(attr.value, dateformat, new Date()), "yyyy-MM-dd");
            }
          }
        }
      } else if (attr.name === "condition") {
        caseFormData.prescription_attributes.condition = attr.value || "";
      } else if (attr.name === "target_after_meal_blood_glucose_min") {
        caseFormData.prescription_attributes.targetAfterMealBloodGlucoseMin = attr.value || "";
      } else if (attr.name === "target_after_meal_blood_glucose_max") {
        caseFormData.prescription_attributes.targetAfterMealBloodGlucoseMax = attr.value || "";
      }else if (attr.name === "patient_training_required") {
        caseFormData.prescription_attributes.Patient_training_required = attr.value === "1" ? true : false;
      } 
      else if (attr.name === "number_of_boxes") {
        caseFormData.prescription_attributes.number_of_boxes = Number(attr.value) ;
       }
      
    
    });
    // caseFormData.prescription_attributes.cp_diuretics = cp_diuretics;
    caseFormData.prescription_attributes.written_date = format(
      new Date(),
      "MM/dd/yyyy",
    );
  }

  if (data.shipments && data.shipments.length > 0) {
    caseFormData.shipments_attributes = [
      {
        ship_to_street: data.shipments[0].ship_to_street,
        ship_to_suite: data.shipments[0].ship_to_suite,
        ship_to_city: data.shipments[0].ship_to_city,
        ship_to_state: data.shipments[0].ship_to_state,
        ship_to_zip: data.shipments[0].ship_to_zip,
      },
    ];
  }

  if (data.doctor) {
    caseFormData.doctor_firstlastname = `${data.doctor.firstname} ${data.doctor.lastname}`;
    caseFormData.doctor_fullname = `${data.doctor.firstname} ${
      data.doctor.middlename || ""
    } ${data.doctor.lastname}`;

    caseFormData.doctor_attributes = {
      practice_id: data.doctor.practice_id,
      global_doctor_id: data.doctor.global_doctor_id,
    } as CaseDoctorProps;
  }

  if (data.practice || data.practice_address) {
    caseFormData.practice_name = `${data.practice.name} - ${data.practice_address.full_address}`;
  }

  if (data.patient) {
    const { patient } = data;

    caseFormData.patient_firstlastname = `${patient.firstname} ${patient.lastname}`;
    caseFormData.patient_fullname = `${patient.firstname} ${
      patient.middlename || ""
    } ${patient.lastname}`;
    caseFormData.patient_dob = patient.dob;

    caseFormData.patient_attributes = {
      global_patient_id: patient.global_patient_id,
      title: patient.title || "",
      firstname: patient.firstname,
      middlename: patient.middlename,
      lastname: patient.lastname,
      gender: patient.gender,
      dob: patient.dob,
      ssn: patient.ssn,
      best_time_to_contact: patient.best_time_to_contact || "",
      // patient_email_id: null,
      // patient_email: data.email_address ? data.email_address.email : "",
      caregiver_name: data.caregiver_name ? data.caregiver_name : "",
      caregiver_phone_number: data.caregiver_phone_number
        ? data.caregiver_phone_number
        : "",
      relationship_to_patient: data.relationship_to_patient
        ? data.relationship_to_patient
        : "",
      weight: data.patient_weight || "",
    } as PatientAttributes;
  }

  if (data.patient_phone_numbers && data.patient_phone_numbers.length > 0) {
    caseFormData.patient_attributes.phone_numbers = [];

    data.patient_phone_numbers.forEach((phone, index) => {
      caseFormData.patient_attributes.phone_numbers[index] =
        {} as PatientPhoneNumber;
      caseFormData.patient_attributes.phone_numbers[index].number =
        phone.number.toString();
      caseFormData.patient_attributes.phone_numbers[index].phone_type =
        phone.phone_type || "cell";
      caseFormData.patient_attributes.phone_numbers[
        index
      ].is_ok_to_leave_message = phone.is_ok_to_leave_message;
      caseFormData.patient_attributes.phone_numbers[index].is_preferred =
        phone.is_preferred;
      caseFormData.patient_attributes.phone_numbers[index].id =
        phone.phone_number_id;
    });
  }

  caseFormData.patient_attributes.address = {
    address_name: "home",
  } as PatientAddress;
  if (data.patient_address) {
    caseFormData.patient_attributes.address.street =
      data.patient_address.street || "";
    caseFormData.patient_attributes.address.suite =
      data.patient_address.suite || "";
    caseFormData.patient_attributes.address.city =
      data.patient_address.city || "";
    caseFormData.patient_attributes.address.state =
      data.patient_address.state || "";
    caseFormData.patient_attributes.address.zip =
      data.patient_address.zip || "";
  }

  if (data.patient_entities_address) {
    caseFormData.patient_attributes.address.address_name =
      data.patient_entities_address.address_name || "";
  }

  caseFormData.insurances_attributes = [{} as InsuranceAttributesProps];
  if (data.insurances && data.insurances.length > 0) {
    data.insurances.forEach((insurance, index) => {
      if (caseFormData.insurances_attributes) {
        caseFormData.insurances_attributes[index] =
          {} as InsuranceAttributesProps;
        caseFormData.insurances_attributes[index].insurance_name =
          insurance.insurance_name;
        caseFormData.insurances_attributes[index].relation_to_patient =
          insurance.relation_to_patient;
        caseFormData.insurances_attributes[index].relation_firstname =
          insurance.relation_firstname || "";
        caseFormData.insurances_attributes[index].relation_middlename =
          insurance.relation_middlename || "";
        caseFormData.insurances_attributes[index].relation_lastname =
          insurance.relation_lastname || "";
        caseFormData.insurances_attributes[index].policy =
          insurance.policy || "";
        caseFormData.insurances_attributes[index].group_number =
          insurance.group_number || "";
        caseFormData.insurances_attributes[index].insurance_number =
          insurance.insurance_number || "";
        caseFormData.insurances_attributes[index].member_id =
          insurance.member_id || "";
        caseFormData.insurances_attributes[index].bin = insurance.bin || "";
        caseFormData.insurances_attributes[index].pcn = insurance.pcn || "";
        caseFormData.insurances_attributes[index].rx_group =
          insurance.rx_group || "";
        caseFormData.insurances_attributes[index].global_insurance_id =
          insurance.global_insurance_id
            ? insurance.global_insurance_id.toString()
            : null;
        caseFormData.insurances_attributes[index].is_primary_insurance =
          index === 0;
        caseFormData.insurances_attributes[index].insurance_priority = index;
        caseFormData.insurances_attributes[index].plan_type =
          insurance.plan_type || "";
      }
    });
  }

  return caseFormData;
}
